// File: frontend/src/components/DownloadPage.js
//
// The download page: the build for the visitor's own OS up top, then every
// platform with its minimums and its store or direct-download links. The
// direct links come from the release channel's binaries.json at runtime;
// the requirements grid and the store links are static, so the prerendered
// page (src/prerender.js) already carries them.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Download, Smartphone } from 'lucide-react';
import MarketingLayout from './marketing/MarketingLayout';
import { Callout } from './marketing/Article';
import StoreBadge from './downloads/StoreBadge';
import { useReleaseInfo } from './downloads/useReleaseInfo';
import {
  detectOS,
  formatFileSize,
  getPlatformFile,
  MIN_REQUIREMENTS,
  PLATFORM_ICONS,
  PLATFORM_META,
  STORE_LINKS,
} from './downloads/releases';

const HERO_CARD = 'bg-[var(--color-paper-bg)] rounded-3xl p-8 md:p-10 shadow-xl border border-[var(--color-ink-subtle)]/10 mb-10 max-w-xl mx-auto text-center';

function DownloadPage() {
  const [releaseChannel, setReleaseChannel] = useState('stable');
  const releaseInfo = useReleaseInfo(releaseChannel);
  // null where there is no navigator (the build-time prerender): no hero.
  const [detectedOS] = useState(detectOS);
  const latestVersion = releaseInfo.version;
  const heroFile = detectedOS && detectedOS !== 'ios' ? getPlatformFile(detectedOS, releaseInfo) : null;

  return (
    <MarketingLayout>
      <main className="px-4 md:px-8 pt-8 pb-20 md:pt-12 md:pb-24 max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl md:text-5xl font-bold font-serif text-[var(--color-ink-main)] mb-4">
            Download Steadily.me
          </h1>
          <p className="text-[var(--color-ink-light)] max-w-2xl mx-auto text-base md:text-lg mb-6">
            Available for most devices made in the last decade - deliberately lightweight, so that tablet in your drawer is probably good enough.
          </p>

          {/* Channel Toggle */}
          <div className="inline-flex items-center gap-2 p-1 bg-[var(--color-paper-bg)] rounded-full shadow-sm border border-[var(--color-ink-subtle)]/10">
            <button
              type="button"
              onClick={() => setReleaseChannel('stable')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                releaseChannel === 'stable'
                  ? 'bg-[var(--color-ink-main)] text-[var(--color-paper-bg)]'
                  : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
              }`}
            >
              Stable
            </button>
            <button
              type="button"
              onClick={() => setReleaseChannel('beta')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                releaseChannel === 'beta'
                  ? 'bg-[var(--color-accent-blue)] text-white'
                  : 'text-[var(--color-ink-light)] hover:text-[var(--color-ink-main)]'
              }`}
            >
              Beta
            </button>
          </div>
        </div>

        {/* Hero — the detected desktop/Android OS, once the feed is in. */}
        {releaseInfo.loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-2 border-[var(--color-ink-subtle)] border-t-[var(--color-ink-main)] rounded-full animate-spin"></div>
            <p className="mt-4 text-[var(--color-ink-light)]">Loading releases...</p>
          </div>
        ) : releaseInfo.error ? (
          <div className="text-center py-12">
            <p className="text-[var(--color-ink-light)]">{releaseInfo.error}</p>
            <p className="text-sm text-[var(--color-ink-subtle)] mt-2">Please try again later.</p>
          </div>
        ) : heroFile && (() => {
          const meta = PLATFORM_META[detectedOS];
          const Icon = PLATFORM_ICONS[detectedOS];
          return (
            <div className={HERO_CARD}>
              <div
                className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
                style={{ backgroundColor: `${meta.accent}1A` }}
              >
                <Icon className="w-8 h-8" style={{ color: meta.accent }} />
              </div>
              <h2 className="text-2xl md:text-3xl font-bold font-serif text-[var(--color-ink-main)] mb-2">
                {meta.heroTagline}
              </h2>
              <p className="text-sm text-[var(--color-ink-subtle)] mb-7">
                {meta.qualifier}
              </p>
              <a
                href={heroFile.url}
                aria-label={`Download Steadily.me${latestVersion ? ' ' + latestVersion : ''} for ${meta.label}${heroFile.sizeBytes ? ', ' + formatFileSize(heroFile.sizeBytes) : ''}`}
                className="group inline-flex items-center gap-3 px-8 py-4 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] text-lg font-bold font-serif rounded-full hover:scale-105 hover:shadow-lg transition-all duration-300"
              >
                <Download className="w-5 h-5" />
                Download for {meta.label}
              </a>
            </div>
          );
        })()}

        {/* iOS hero — App Store CTA. iOS ships only through the App Store,
            so it has no binaries.json entry and no PLATFORM_META row; the
            card is self-contained and needs no feed. */}
        {detectedOS === 'ios' && (
          <div className={HERO_CARD}>
            <div
              className="w-16 h-16 rounded-2xl mx-auto mb-5 flex items-center justify-center"
              style={{ backgroundColor: '#0A84FF1A' }}
            >
              <Smartphone className="w-8 h-8" style={{ color: '#0A84FF' }} />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold font-serif text-[var(--color-ink-main)] mb-2">
              On your iPhone and iPad
            </h2>
            <p className="text-sm text-[var(--color-ink-subtle)] mb-7">
              Free on the App Store · iOS / iPadOS 15+
            </p>
            <StoreBadge
              url={STORE_LINKS.iosAppStore}
              badge="/images/badges/app-store.svg"
              label="Download on the App Store"
              imgClass="h-14"
            />
            <p className="text-sm text-[var(--color-ink-subtle)] mt-6">
              Or{' '}
              <Link to="/login" className="underline underline-offset-2 hover:text-[var(--color-ink-main)] transition-colors">
                open the web app
              </Link>
              .
            </p>
          </div>
        )}

        {/* Spec + download cards — every platform with its minimums and,
            where a build exists in binaries.json, its download link. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {MIN_REQUIREMENTS.map(req => {
            const Icon = req.icon;
            const file = PLATFORM_META[req.key] ? getPlatformFile(req.key, releaseInfo) : null;
            return (
              <div
                key={req.key}
                className="flex flex-col bg-[var(--color-paper-bg)] rounded-3xl p-7 shadow-sm border border-[var(--color-ink-subtle)]/10 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span
                    className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: `${req.accent}1A` }}
                  >
                    <Icon className="w-5 h-5" style={{ color: req.accent }} />
                  </span>
                  <div>
                    <h3 className="font-bold font-serif text-[var(--color-ink-main)]">{req.label}</h3>
                    <p className="text-sm text-[var(--color-ink-light)]">{req.headline}</p>
                  </div>
                </div>
                <ul className="space-y-2 mb-4">
                  {req.details.map(detail => (
                    <li key={detail} className="flex items-start gap-2 text-sm text-[var(--color-ink-main)]">
                      <CheckCircle2 className="w-4 h-4 text-[var(--color-accent-green)] flex-shrink-0 mt-0.5" />
                      <span>{detail}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-[var(--color-ink-subtle)] italic leading-relaxed">
                  {req.examples}
                </p>
                <div className="mt-auto pt-5 flex flex-wrap items-center gap-2">
                  {(req.stores || []).map(store => (
                    <StoreBadge
                      key={store.label}
                      url={store.url}
                      badge={store.badge}
                      label={store.label}
                      imgClass={store.badgeClass}
                    />
                  ))}
                  {file && (
                    <a
                      href={file.url}
                      aria-label={`Download Steadily.me${latestVersion ? ' ' + latestVersion : ''} for ${req.label}`}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--color-ink-main)] text-[var(--color-paper-bg)] text-sm font-medium rounded-full hover:scale-105 hover:shadow-md transition-all"
                    >
                      <Download className="w-4 h-4" />
                      Download
                    </a>
                  )}
                </div>
                {req.guide && (
                  <p className="mt-4">
                    <Link
                      to={req.guide}
                      className="inline-flex items-center gap-1 text-sm text-[var(--color-ink-light)] underline underline-offset-2 hover:text-[var(--color-ink-main)] transition-colors"
                    >
                      Setting up an always-on display <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </p>
                )}
              </div>
            );
          })}
        </div>

        {/* Single version line */}
        {!releaseInfo.loading && !releaseInfo.error && latestVersion && (
          <p className="text-center text-xs text-[var(--color-ink-subtle)] mt-8 tracking-wide">
            Latest · v{latestVersion} on the <span className="font-medium">{releaseChannel}</span> channel
          </p>
        )}

        <Callout title="Want it on all the time?" className="mt-12 max-w-3xl mx-auto">
          <p>
            An Android photo frame, an old tablet, a Raspberry Pi or a spare PC can boot straight into Steadily.me
            and stay there, on a wall or a desk. The setup guide walks through each one, including how to make the
            app the device's home screen.
          </p>
          <p>
            <Link to="/install" className="inline-flex items-center gap-2 font-serif font-bold underline underline-offset-4">
              Set up an always-on display <ArrowRight className="w-4 h-4" />
            </Link>
          </p>
        </Callout>

        {/* Store-badge trademark attribution, required by the badge
            usage guidelines. */}
        <p className="text-center text-[11px] text-[var(--color-ink-subtle)] mt-12 max-w-2xl mx-auto leading-relaxed">
          Apple, the Apple logo, App Store and Mac App Store are trademarks of Apple Inc. Google Play and the Google Play logo are trademarks of Google LLC. Samsung and Galaxy Store are trademarks of Samsung Electronics Co., Ltd.
        </p>
      </main>
    </MarketingLayout>
  );
}

export default DownloadPage;
